import { Axios } from "api/axios";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import Colors from "Constants/Color";
import { tableStyles } from "Constants/Style";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { api } from "helpers/api_url";
import React, { Fragment, useEffect, useState, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";
import {
    Button,
    Col,
    Container,
    Form,
    Row,
    Table,
    Spinner,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ViewBagsModal from "./ViewBagsModal";
import { showConfirm } from "utils/swalHelper";

const CancelOrder: React.FC = () => {
    document.title = "Cancel Order";
    const { id } = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>('');
    const [data, setData] = useState<any>({});
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [cancelling, setCancelling] = useState<boolean>(false);
    const [show, setShow] = useState<boolean>(false);
    const [viewBagsForItem, setViewBagsForItem] = useState<any[]>([]);

    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            setLoading(true);
            Axios.get(`${api?.order?.order_by_id}/${id}`).then((result) => {
                setData(result?.data?.data || {})
            }).catch((err) => {
                toast.error("Failed to fetch Order data")
                setError("Failed to fetch Order data")
            }).finally(() => {
                setLoading(false);
            })
        }
    }, [id])

    const handleSelectItem = (itemId: string) => {
        // Find the item to check if it's cancelled
        const item = data?.order_items?.find((item: any) => {
            const id = item.order_item_id || item.id || item.order_items_id;
            return id === itemId;
        });

        // Prevent selecting cancelled items
        if (item?.order_item_status === "cancelled") {
            return;
        }

        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    const handleSelectAll = () => {
        // Get only non-cancelled items
        const nonCancelledItems = data?.order_items?.filter((item: any) => item?.order_item_status !== "cancelled") || [];
        const nonCancelledItemIds = nonCancelledItems.map((item: any) => item.order_item_id || item.id || item.order_items_id);

        // Check if all non-cancelled items are already selected
        const allNonCancelledSelected = nonCancelledItemIds.length > 0 &&
            nonCancelledItemIds.every((id: string) => selectedItems.includes(id));

        if (allNonCancelledSelected) {
            // Deselect all non-cancelled items
            setSelectedItems(prev => prev.filter(id => !nonCancelledItemIds.includes(id)));
        } else {
            // Select all non-cancelled items (keep any already selected cancelled items)
            setSelectedItems(prev => {
                const cancelledItemIds = prev.filter(id => !nonCancelledItemIds.includes(id));
                return [...cancelledItemIds, ...nonCancelledItemIds];
            });
        }
    };

    // Check if all non-cancelled items are selected
    const allNonCancelledSelected = useMemo(() => {
        if (!data?.order_items || data.order_items.length === 0) {
            return false;
        }
        const nonCancelledItems = data.order_items.filter((item: any) => item?.order_item_status !== "cancelled");
        const nonCancelledItemIds = nonCancelledItems.map((item: any) => item.order_item_id || item.id || item.order_items_id);

        return nonCancelledItemIds.length > 0 &&
            nonCancelledItemIds.every((id: string) => selectedItems.includes(id));
    }, [data?.order_items, selectedItems]);

    // Calculate totals for remaining items (excluding selected items)
    const recalculatedTotals = useMemo(() => {
        if (!data?.order_items || selectedItems.length === 0) {
            return {
                subtotal_before_discount: Number(data?.subtotal_before_discount) || 0,
                total_item_discounts: Number(data?.total_item_discounts) || 0,
                net_taxable_value: Number(data?.net_taxable_value) || 0,
                total_gst: Number(data?.total_gst) || 0,
                total_amount: Number(data?.total_amount) || 0,
            };
        }

        // Get selected items
        const selectedItemsData = data.order_items.filter((item: any) => {
            const itemId = item.order_item_id || item.id || item.order_items_id;
            return selectedItems.includes(itemId);
        });

        // Calculate totals for selected items
        const selectedSubtotalBeforeDiscount = selectedItemsData.reduce((sum: number, item: any) => {
            return sum + (Number(item.amount) || 0);
        }, 0);

        const selectedTotalItemDiscounts = selectedItemsData.reduce((sum: number, item: any) => {
            return sum + (Number(item.discount_amount) || 0);
        }, 0);

        const selectedNetTaxableValue = selectedSubtotalBeforeDiscount - selectedTotalItemDiscounts;

        const selectedTotalGst = selectedItemsData.reduce((sum: number, item: any) => {
            return sum + (Number(item.gst_amount) || 0);
        }, 0);

        const selectedTotalAmount = selectedItemsData.reduce((sum: number, item: any) => {
            return sum + (Number(item.total_amount) || 0);
        }, 0);

        // Calculate remaining totals by subtracting selected items from original totals
        const originalSubtotal = Number(data?.subtotal_before_discount) || 0;
        const originalDiscounts = Number(data?.total_item_discounts) || 0;
        const originalNetTaxable = Number(data?.net_taxable_value) || 0;
        const originalGst = Number(data?.total_gst) || 0;
        const originalTotal = Number(data?.total_amount) || 0;

        return {
            subtotal_before_discount: parseFloat((originalSubtotal - selectedSubtotalBeforeDiscount).toFixed(2)),
            total_item_discounts: parseFloat((originalDiscounts - selectedTotalItemDiscounts).toFixed(2)),
            net_taxable_value: parseFloat((originalNetTaxable - selectedNetTaxableValue).toFixed(2)),
            total_gst: parseFloat((originalGst - selectedTotalGst).toFixed(2)),
            total_amount: parseFloat((originalTotal - selectedTotalAmount).toFixed(2)),
        };
    }, [data, selectedItems]);

    // Determine order status based on selection
    const orderStatus = useMemo(() => {
        if (!data?.order_items || selectedItems.length === 0) {
            return null;
        }
        const totalItems = data.order_items.length;
        const selectedCount = selectedItems.length;

        // If all items are selected, status is "cancelled"
        // If only some items are selected, status is "partially cancelled"
        return selectedCount === totalItems ? "cancelled" : "partially cancelled";
    }, [data, selectedItems]);

    const handleCancelOrder = async () => {
        if (selectedItems.length === 0) {
            toast.error("Please select at least one item to cancel");
            return;
        }

        const statusText = selectedItems.length === data?.order_items?.length
            ? "cancel this order"
            : "cancel selected items";

        const result = await showConfirm(
             `Do you really want to ${statusText}?`,
            "Are you sure?",
            "Yes, cancel it!",
            "No, keep it"
        );
        if (!result?.isConfirmed) return;
        if (result?.isConfirmed) {
            try {
                setCancelling(true);
                const payload = {
                    order_item_ids: selectedItems,
                    order_status: orderStatus,
                    subtotal_before_discount: recalculatedTotals.subtotal_before_discount,
                    total_item_discounts: recalculatedTotals.total_item_discounts,
                    net_taxable_value: recalculatedTotals.net_taxable_value,
                    total_gst: recalculatedTotals.total_gst,
                    total_amount: recalculatedTotals.total_amount,
                };

                await Axios.patch(`/order/cancel/${id}`, payload);
                const successMessage = "Selected items have been cancelled.";
                toast.success(successMessage);
                navigate("/order-list");
            } catch (err: any) {
                const errorMessage = err?.response?.data?.error || err?.response?.data?.message || "Failed to cancel order. Try again.";
                // Swal.fire("Error!", errorMessage, "error");
                toast.error(errorMessage);
            } finally {
                setCancelling(false);
            }
        }
 
    };

    return (
        <Fragment>
            <style>
                {`
                    .cancelled-checkbox input[type="checkbox"] {
                        border: 2px solid red !important;
                        border-radius: 4px;
                    }
                `}
            </style>
            <div
                className="page-content px-4"
                style={{ backgroundColor: "#e9ecef", minHeight: "100vh" }}
            >
                <Container
                    fluid
                    style={{
                        backgroundColor: "#fff",
                        padding: "30px",
                        borderRadius: "10px",
                        boxShadow: "0 0 15px rgba(0,0,0,0.1)",
                    }}
                >
                    <LoaderErrorWrapper loading={loading} error={error}>
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mb-4">
                            <div className="d-flex align-items-center gap-2">
                                <Link to="/order-list" style={{ color: "inherit", textDecoration: "none" }}>
                                    <i className="bi bi-arrow-left" style={{ fontSize: "1.2rem", cursor: "pointer" }} aria-label="Back" />
                                </Link>
                                <h6 className="card-title mb-0">Cancel Order</h6>
                            </div>

                            <div className="text-muted">
                                <Link to="/order-list" style={{ color: "inherit", textDecoration: "none" }}>
                                    Order
                                </Link>{" "}&gt;{" "}
                                <span>Cancel Order</span>
                            </div>
                        </div>
                        <div className="mb-2">
                            <h6 className="mb-3" style={{ fontWeight: "600", color: "#212529" }}>
                                Order Details
                            </h6>
                            <Row>
                                <Col lg={3} className="mb-2">
                                    <div className="d-flex gap-1 align-items-center">
                                        <span style={{ fontWeight: "500" }}>Order No.:</span>
                                        <span style={{ color: "#212529" }}>{data.order_no || '-'}</span>
                                    </div>
                                </Col>
                                <Col lg={3} className="mb-2">
                                    <div className="d-flex gap-1 align-items-center">
                                        <span style={{ fontWeight: "500" }}>Quotation No.:</span>
                                        <span style={{ color: "#212529" }}>{data?.quotation_no || '-'}</span>
                                    </div>
                                </Col>
                                <Col lg={3} className="mb-2">
                                    <div className="d-flex gap-1 align-items-center">
                                        <span style={{ fontWeight: "500" }}>Order Date:</span>
                                        <span style={{ color: "#212529" }}>{data?.order_date?.toString()?.split('T')[0] || '-'}</span>
                                    </div>
                                </Col>

                                <Col lg={3} className="mb-2">
                                    <div className="d-flex gap-1 align-items-center">
                                        <span style={{ fontWeight: "500" }}>Customer:</span>
                                        <span style={{ color: "#212529" }}>{data?.customer_details?.customer_name || '-'}</span>
                                    </div>
                                </Col>
                                <Col lg={6} className="mb-2">
                                    <div className="d-flex gap-1 align-items-center">
                                        <span style={{ fontWeight: "500" }}>Dispatch From Warehouse:</span>
                                        <span style={{ color: "#212529" }}>{data?.warehouse?.warehouse_name || '-'}</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0" style={{ fontWeight: "600", color: "#212529" }}>
                                    Item Details
                                </h6>
                                {data?.order_items?.length > 0 && (
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Check
                                            type="checkbox"
                                            label="Select All"
                                            checked={allNonCancelledSelected}
                                            onChange={handleSelectAll}
                                        />
                                        <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                                            ({selectedItems.length} selected)
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="mb-2">
                                <Table hover responsive className="align-middle text-center">
                                    <thead>
                                        <tr>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "50px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={allNonCancelledSelected}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>Sr No.</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Category</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Sub Category</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Item Name</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Item Code</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Unit</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Quantity</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Bags</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Rate</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Amount</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Discount</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Discount Amount</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>GST%</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>GST Amount</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Total Amount</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Status</th>
                                            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.order_items?.length === 0 ? (
                                            <tr>
                                                <td colSpan={17} style={{ ...tableStyles.cell, textAlign: 'center' }} className="py-3">
                                                    No Items added. Please add at least one item.
                                                </td>
                                            </tr>
                                        ) : (
                                            data?.order_items?.map((row: any, idx: number) => {
                                                const itemId = row.order_item_id || row.id || row.order_items_id;
                                                const isSelected = selectedItems.includes(itemId);

                                                return (
                                                    <tr
                                                        key={idx}
                                                        style={{
                                                            backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                                                        }}
                                                    >
                                                        <td style={tableStyles.cell}>
                                                            <Form.Check
                                                                className={row?.order_item_status === "cancelled" ? "cancelled-checkbox" : ""}
                                                                disabled={row?.order_item_status === "cancelled"}
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectItem(itemId)}
                                                            />
                                                        </td>

                                                        <td style={tableStyles.cell}>{idx + 1}</td>

                                                        {/* Category */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.category?.category_name || "-"}</span>
                                                        </td>

                                                        {/* Sub Category */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.sub_category?.sub_category_name || "-"}</span>
                                                        </td>

                                                        {/* Item Name */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.item?.item_name || "-"}</span>
                                                        </td>

                                                        {/* Item Code */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.item_code || "-"}</span>
                                                        </td>

                                                        {/* Unit */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.unit?.unit_name || row?.unit || "-"}</span>
                                                        </td>

                                                        {/* Quantity */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.quantity || "-"}</span>
                                                        </td>

                                                        {/* Bags */}
                                                        <td style={tableStyles.cell}>
                                                            <div style={{ textAlign: 'center' }}>
                                                                {row?.bags && row.bags.length > 0 ? (
                                                                    <VisibilityOutlinedIcon
                                                                        style={{
                                                                            color: Colors?.bg_blue,
                                                                            cursor: 'pointer',
                                                                            fontSize: '1.5rem'
                                                                        }}
                                                                        onClick={() => {
                                                                            setViewBagsForItem(row.bags);
                                                                            setShow(true);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* Rate */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.rate || "-"}</span>
                                                        </td>

                                                        {/* Amount */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row.amount || "-"}</span>
                                                        </td>

                                                        {/* Discount */}
                                                        <td style={tableStyles.cell}>
                                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                                                                <span>{row?.discount || "-"}</span>
                                                                <span>{row?.discount_type === 'percentage' ? '%' : "₹"}</span>
                                                            </div>
                                                        </td>

                                                        {/* Discount Amount */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row.discount_amount || "-"}</span>
                                                        </td>

                                                        {/* GST */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row?.gst?.gst_percent || "-"}</span>
                                                        </td>

                                                        {/* GST Amount */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row.gst_amount || "-"}</span>
                                                        </td>

                                                        {/* Total Amount */}
                                                        <td style={tableStyles.cell}>
                                                            <span>{row.total_amount || "-"}</span>
                                                        </td>
                                                        <td style={{ ...tableStyles.cell }}>
                                                            <OrderStatusBadge
                                                                status={row?.order_item_status || "-"}
                                                            />
                                                        </td>
                                                        {/* Remarks */}
                                                        <td className="remark-cell" style={tableStyles.cell}>
                                                            <TruncatedTextCell
                                                                text={row?.remark}
                                                                maxWidth="300px"
                                                                maxLines={4}
                                                                rowIndex={idx}
                                                                fallbackText="-"
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                        <Row className="mb-4">
                            <Col lg={7} className="mb-3">
                                <div style={{
                                    borderRadius: "4px 4px 0 0",
                                }}>
                                    <div className="d-flex align-items-center gap-2 mb-0 py-2 px-1" style={{ background: Colors?.grey, borderRadius: "6px 6px 0 0", }}>
                                        <i className="bi bi-chevron-down" style={{ fontSize: "0.675rem", color: "#000", }}></i>
                                        <h6 className="mb-0">Remark</h6>
                                    </div>
                                    <div
                                        style={{
                                            border: `1px solid ${Colors?.grey}`,
                                            borderRadius: "0 0 6px 6px",
                                            padding: "12px",
                                            minHeight: "167px",
                                            height: '100%',
                                            backgroundColor: "#fff",
                                            color: "#212529",
                                        }}
                                    >
                                        {data.remark}
                                    </div>
                                </div>
                            </Col>
                            <Col lg={5} className="mb-3">
                                <div
                                    style={{
                                        backgroundColor: "#e3f2fd",
                                        padding: "20px",
                                        borderRadius: "8px",
                                        border: "1px solid #90caf9"
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>Subtotal (Before Discount):</span>
                                        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#212529" }}>
                                            ₹ {recalculatedTotals.subtotal_before_discount.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>Total Item Discounts:</span>
                                        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#212529" }}>
                                            ₹ {recalculatedTotals.total_item_discounts.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>Net Taxable Value:</span>
                                        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#212529" }}>
                                            ₹ {recalculatedTotals.net_taxable_value.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span style={{ fontSize: "0.875rem", color: "#212529" }}>Total GST:</span>
                                        <span style={{ fontSize: "0.875rem", fontWeight: "500", color: "#212529" }}>
                                            ₹ {recalculatedTotals.total_gst.toFixed(2)}
                                        </span>
                                    </div>
                                    <hr style={{ margin: "12px 0", borderColor: "#90caf9" }} />
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span style={{ fontSize: "1rem", fontWeight: "600", color: "#212529" }}>Total Amount:</span>
                                        <span style={{ fontSize: "1rem", fontWeight: "700", color: "#212529" }}>
                                            ₹ {recalculatedTotals.total_amount.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <div className="d-flex justify-content-center gap-3 py-3">
                            <Button
                                variant="light"
                                onClick={() => navigate("/order-list")}
                                disabled={cancelling}
                                style={{
                                    backgroundColor: "#f8f9fa",
                                    border: "1px solid #ced4da",
                                    color: "#212529",
                                    minWidth: "100px",
                                }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleCancelOrder}
                                disabled={cancelling || selectedItems.length === 0}
                                style={{
                                    minWidth: "150px",
                                }}
                            >
                                {cancelling ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Cancelling...
                                    </>
                                ) : (
                                    "Cancel Selected Items"
                                )}
                            </Button>
                        </div>
                    </LoaderErrorWrapper>
                </Container>
            </div>
            <ViewBagsModal
                show={show}
                handleClose={() => setShow(false)}
                bags={viewBagsForItem}
            />
        </Fragment>
    );
};

export default CancelOrder;

